import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { ACCOUNT } from '../../infrastructure/config/api';
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Fade,
  Tooltip
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon,
  AccountBalance as BalanceIcon,
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { motion, isValidMotionProp } from 'framer-motion';
import { styled } from '@mui/material/styles';
import api from '../../infrastructure/config/api';

const POLLING_INTERVAL = 30000; // 30 seconds

const formatBalance = (value, currency = 'USDT', showCurrency = true) => {
  if (value === null || value === undefined) return '—';

  const number = parseFloat(value);
  if (isNaN(number)) return '—';

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'decimal',
    useGrouping: true
  }).format(number) + (showCurrency ? ` ${currency}` : '');
};

const AccountOverviewCard = ({ userId, totalPnl = 0 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showBalances, setShowBalances] = useState(true);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  // Create styled motion components that filter out invalid props
  const MotionDiv = styled(motion.div)(
    { shouldForwardProp: (prop) => isValidMotionProp(prop) || prop === 'children' }
  );

  const syncBalances = useCallback(async () => {
    try {
      console.log('Syncing balances with Binance...');
      await api.post(ACCOUNT.SYNC_BALANCES);
      console.log('Successfully synced balances with Binance');
      return true;
    } catch (error) {
      console.error('Error syncing balances:', error);
      enqueueSnackbar('Lỗi đồng bộ số dư từ Binance', { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar]);

  const fetchBalances = useCallback(async (isManualRefresh = false, forceSync = false) => {
    if (!userId) {
      console.log('⚠️ [AccountOverviewCard] No userId provided, skipping balance fetch');
      return;
    }

    if (isManualRefresh) {
      console.log('🔄 [AccountOverviewCard] Manual refresh triggered');
      setRefreshing(true);
    } else {
      console.log(`📊 [AccountOverviewCard] Initial balance fetch for userId: ${userId}`);
      setLoading(true);
    }
    try {
      // If this is a manual refresh, sync with Binance first
      if (isManualRefresh || forceSync) {
        const syncSuccess = await syncBalances();
        if (!syncSuccess) {
          console.log('⚠️ [AccountOverviewCard] Balance sync failed, using cached data');
        }
      }

      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      console.log('🔍 [AccountOverviewCard] Fetching balances...');
      console.log('  - UserId:', userId);
      console.log('  - Token exists:', !!currentToken);
      console.log('  - User in localStorage:', currentUser ? JSON.parse(currentUser) : null);

      const response = await api.get(ACCOUNT.BALANCES);
      console.log('✅ [AccountOverviewCard] API Response:', response.data);
      console.log('  - Response userId:', response.data?.data?.user_id);

      if (response.data.success && response.data.data) {
        console.log('Setting balances:', response.data.data);
        setBalances(response.data.data);
      } else {
        console.error('Failed to load balance data:', response.data);
      }
    } catch (err) {
      console.error('Error fetching balances:', err);
      enqueueSnackbar('Error loading balance data', { variant: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId, syncBalances, enqueueSnackbar]);

  // Reset state khi userId thay đổi - QUAN TRỌNG để tránh hiển thị data của user khác
  useEffect(() => {
    console.log('🔄 UserId changed, clearing balances state...');
    setBalances(null);
    setLoading(true);
  }, [userId]);

  // Initial fetch and polling
  useEffect(() => {
    if (!userId) {
      console.log('⚠️ No userId, skipping balance fetch');
      return;
    }

    console.log(`📊 Fetching balances for userId: ${userId}`);
    fetchBalances();

    // Set up polling
    const intervalId = setInterval(() => {
      fetchBalances();
    }, POLLING_INTERVAL);

    return () => {
      console.log('🧹 Cleaning up polling interval');
      clearInterval(intervalId);
    };
  }, [userId, fetchBalances]);

  const handleRefresh = async (e) => {
    e.stopPropagation();
    await fetchBalances(true, true); // Force sync with Binance
  };

  const getStatusColor = (value) => {
    if (value === null || value === undefined) return 'text.secondary';
    const num = parseFloat(value);
    if (isNaN(num)) return 'text.secondary';
    return num >= 0 ? 'success.main' : 'error.main';
  };

  const getStatusIcon = (value) => {
    if (value === null || value === undefined) return null;
    const num = parseFloat(value);
    if (isNaN(num)) return null;

    if (num > 0) {
      return <CheckCircleIcon color="success" sx={{ fontSize: '1.2rem' }} />;
    } else if (num < 0) {
      return <WarningIcon color="error" sx={{ fontSize: '1.2rem' }} />;
    }
    return <InfoIcon color="info" sx={{ fontSize: '1.2rem' }} />;
  };

  const renderLastUpdated = () => {
    if (!balances?.updated_at) return null;

    const updatedAt = new Date(balances.updated_at);
    const now = new Date();
    const diffInMinutes = Math.floor((now - updatedAt) / (1000 * 60));

    let statusText = 'Vừa cập nhật';
    let statusColor = 'rgba(255, 255, 255, 0.85)';

    if (diffInMinutes > 5) {
      statusText = 'Dữ liệu cũ';
      statusColor = 'rgba(255, 214, 102, 0.95)';
    }

    if (diffInMinutes > 15) {
      statusText = 'Dữ liệu quá cũ';
      statusColor = 'rgba(255, 120, 120, 0.95)';
    }

    return (
      <Typography
        variant="caption"
        color={statusColor}
        noWrap
        sx={{
          display: 'block',
          mt: 0.5,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          fontSize: isMobile ? '0.78rem' : undefined
        }}
      >
        {statusText} · {format(updatedAt, 'HH:mm:ss')}
      </Typography>
    );
  };

  // Skeleton loading state
  if (loading && !balances) {
    return (
      <MotionDiv
        initial="hidden"
        animate="visible"
        variants={cardVariants}
      >
        <Card
          elevation={3}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[100]} 100%)`,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'visible',
            position: 'relative',
            '&:hover': {
              boxShadow: theme.shadows[8]
            },
            transition: 'all 0.3s ease-in-out'
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Skeleton variant="text" width={180} height={32} />
              <Box>
                <Skeleton variant="circular" width={36} height={36} />
              </Box>
            </Box>
            <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 3 }} />
            <Skeleton variant="text" width="80%" height={24} />
          </CardContent>
        </Card>
      </MotionDiv>
    );
  }

  const toggleShowBalances = () => {
    setShowBalances(prev => !prev);
  };

  return (
    <MotionDiv
      initial="hidden"
      animate="visible"
      variants={cardVariants}
    >
      <Card
        elevation={3}
        sx={{
          borderRadius: 3,
          background: `
            radial-gradient(1200px 600px at 10% -20%, rgba(88, 199, 250, 0.18), transparent 55%),
            radial-gradient(800px 500px at 120% 10%, rgba(255, 196, 88, 0.18), transparent 60%),
            radial-gradient(900px 600px at 30% 120%, rgba(120, 255, 214, 0.16), transparent 55%),
            radial-gradient(700px 500px at 80% 120%, rgba(255, 120, 166, 0.16), transparent 55%),
            radial-gradient(600px 500px at 40% 40%, rgba(150, 120, 255, 0.12), transparent 55%),
            radial-gradient(600px 500px at 70% 60%, rgba(90, 255, 120, 0.10), transparent 55%),
            radial-gradient(700px 600px at 20% 70%, rgba(255, 90, 90, 0.10), transparent 60%),
            linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[100]} 100%)
          `,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'visible',
          position: 'relative',
          '&:hover': {
            boxShadow: theme.shadows[8]
          },
          transition: 'all 0.3s ease-in-out'
        }}
      >
        {loading && (
          <Fade in={loading}>
            <LinearProgress
              color="primary"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                height: 3
              }}
            />
          </Fade>
        )}

        <CardContent>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }}>
            <Box>
              <Typography
                variant={isMobile ? 'caption' : 'subtitle1'}
                color="text.secondary"
                sx={{ mb: 0.5, whiteSpace: 'nowrap', fontSize: isMobile ? '0.85rem' : undefined }}
              >
                Tổng tài sản
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h4'}
                  sx={{
                    fontWeight: 700,
                    color: getStatusColor(balances?.wallet_balance),
                    lineHeight: 1.1,
                    mr: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: isMobile ? '1.25rem' : undefined
                  }}
                >
                  {showBalances
                    ? formatBalance(balances?.wallet_balance, balances?.currency)
                    : '•••••••'}
                </Typography>
                {getStatusIcon(balances?.wallet_balance)}
              </Box>
              {renderLastUpdated()}
            </Box>

            <Box sx={{ display: 'flex' }}>
              <Tooltip title={showBalances ? 'Ẩn số dư' : 'Hiện số dư'}>
                <IconButton
                  onClick={toggleShowBalances}
                  size="small"
                  sx={{
                    mr: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                >
                  {showBalances ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Làm mới">
                <MotionDiv
                  animate={{ rotate: refreshing ? 360 : 0 }}
                  transition={{ duration: 1, ease: 'linear', repeat: refreshing ? Infinity : 0 }}
                  style={{ display: 'inline-flex' }}
                >
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    disabled={refreshing}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    <RefreshIcon className={refreshing ? 'spin' : ''} />
                  </IconButton>
                </MotionDiv>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BalanceIcon sx={{
                    color: 'text.secondary',
                    mr: 1,
                    fontSize: '1.1rem'
                  }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: isMobile ? '0.85rem' : undefined, whiteSpace: 'nowrap' }}
              >
                Số dư còn lại
              </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ fontSize: isMobile ? '0.95rem' : undefined, whiteSpace: 'nowrap', textAlign: 'right' }}
                >
                  {showBalances
                    ? formatBalance(
                      (parseFloat(balances?.wallet_balance || 0) - Math.abs(totalPnl || 0) - Math.abs(parseFloat(balances?.margin_balance || 0))).toFixed(2),
                      balances?.currency,
                      true
                    )
                    : '•••••••'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BalanceIcon sx={{
                    color: `${getStatusColor(balances?.margin_balance)}.main`,
                    mr: 1,
                    fontSize: '1.1rem',
                    opacity: 0.7
                  }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.85rem' : undefined, whiteSpace: 'nowrap' }}
                  >
                    Đang sử dụng
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color={getStatusColor(balances?.margin_balance) + '.main'}
                  sx={{ fontSize: isMobile ? '0.95rem' : undefined, whiteSpace: 'nowrap', textAlign: 'right' }}
                >
                  {showBalances
                    ? formatBalance(Math.abs(balances?.margin_balance || 0), balances?.currency, true)
                    : '•••••••'}
                </Typography>
              </Box>

              {/* Total PnL */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.85rem' : undefined, whiteSpace: 'nowrap' }}
                  >
                    Tổng Lãi/Lỗ
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color={totalPnl >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: isMobile ? '0.95rem' : undefined, whiteSpace: 'nowrap', textAlign: 'right' }}
                >
                  {showBalances
                    ? `${totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)} USDT`
                    : '•••••••'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WalletIcon sx={{ color: 'primary.main', mr: 1, fontSize: '1.1rem' }} />
                  <Typography
                    variant="body2"
                    color="primary"
                    fontWeight={500}
                    sx={{ fontSize: isMobile ? '0.85rem' : undefined, whiteSpace: 'nowrap' }}
                  >
                    Tài sản thực tế
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="primary"
                  sx={{ fontSize: isMobile ? '0.95rem' : undefined, whiteSpace: 'nowrap', textAlign: 'right' }}
                >
                  {showBalances
                    ? formatBalance(
                      (parseFloat(balances?.wallet_balance || 0) - Math.abs(totalPnl || 0)).toFixed(2),
                      balances?.currency,
                      true
                    )
                    : '•••••••'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {balances?.updated_at && (
            <Box sx={{ mt: 2, textAlign: 'right', display: isMobile ? 'none' : 'block' }}>
              <Typography
                variant="caption"
                color="rgba(255, 255, 255, 0.75)"
                noWrap
                sx={{
                  fontSize: '0.7rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                Cập nhật · {format(new Date(balances.updated_at), 'HH:mm:ss')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <style jsx global>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </MotionDiv>
  );
};

export default AccountOverviewCard;
